/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 20 2025
 **/

import { deleteDB, openDB } from "idb";
import { PersistedClient, Persister } from "@tanstack/react-query-persist-client";

import { Environments, Properties } from "@/frameworks/libraries";

class QueryPersisterStatic {
	private static instance: QueryPersisterStatic;
	private persister: Persister;
	private static readonly DB_NAME = "rsupportReactQuery";
	private static readonly DB_OBJECT_STORE_NAME = "reactQuery";
	private constructor() {
		QueryPersisterStatic.instance = this;
		this.persister = createIDBPersister(Properties.browserTabID);
	}

	public static getInstance() {
		if (!QueryPersisterStatic.instance) {
			QueryPersisterStatic.instance = new QueryPersisterStatic();
		}
		return QueryPersisterStatic.instance;
	}

	public getPersister() {
		return this.persister;
	}

	public static get DBName() {
		return QueryPersisterStatic.DB_NAME;
	}

	public static get DBObjectStoreName() {
		return QueryPersisterStatic.DB_OBJECT_STORE_NAME;
	}
}

export default QueryPersisterStatic;

/**
 * @description
 * idbValidKey를 사용자 id가 아닌 BrowserTabID로 다루는 이유
 * 서비스 정책상 별도 탭에서도 동일한 사용자가 접근할 수 있어 동일한 공용메모리를 이용하는게 올바르나,
 * API와 상태는 탭기준의 고유 정보가 들어있어 BrowserTabID로 다룸
 */
function createIDBPersister(idbValidKey: IDBValidKey = "reactQueryKey") {
	const version = Environments.env("ROOT_VERSION") as string;

	const database = openDB(QueryPersisterStatic.DBName, parseInt(version.replace(/\./g, "")), {
		upgrade(db, oldVersion, newVersion) {
			console.log("upgrade", db, oldVersion, newVersion);
			if (oldVersion !== newVersion) {
				if (db.objectStoreNames.contains(QueryPersisterStatic.DBObjectStoreName)) {
					db.deleteObjectStore(QueryPersisterStatic.DBObjectStoreName);
				}
				db.createObjectStore(QueryPersisterStatic.DBObjectStoreName);
			}
		}
	});

	const getDatabase = async () => {
		try {
			return await database;
		} catch (error) {
			console.error("Database connection error:", error);
			// 에러 발생 시 기존 데이터베이스 삭제 후 재시도
			if (error instanceof Error && error.name === "VersionError") {
				const dbName = QueryPersisterStatic.DBName;
				await deleteDB(dbName);
				return await openDB(dbName, parseInt(version.replace(/\./g, "")), {
					upgrade(db) {
						db.createObjectStore(QueryPersisterStatic.DBObjectStoreName);
					}
				});
			}
			throw error;
		}
	};

	return {
		restoreClient: async () => {
			const db = await getDatabase();
			return db.get(QueryPersisterStatic.DBObjectStoreName, idbValidKey) || null;
		},
		persistClient: async (client: PersistedClient) => {
			const db = await getDatabase();
			db.put(QueryPersisterStatic.DBObjectStoreName, client, idbValidKey);
		},
		removeClient: async () => {
			const db = await getDatabase();
			db.delete(QueryPersisterStatic.DBObjectStoreName, idbValidKey);
		}
	} as Persister;
}
